import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Bell,
  Plus,
  RefreshCw,
  Trash2,
  AlertCircle,
  CheckCircle,
  X,
  Info,
  AlertTriangle,
  Shield,
  Building2,
  Send,
  Globe,
} from 'lucide-react';
import Header from '../../components/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { apiClient, Tenant } from '../../services/api';
import type { Notification, NotificationType } from '../../types/notification';

const TYPE_OPTIONS: { value: NotificationType; label: string; icon: typeof Info; color: string }[] = [
  { value: 'INFO', label: 'Informativo', icon: Info, color: 'text-accent-1' },
  { value: 'UPDATE', label: 'Atualização', icon: RefreshCw, color: 'text-success' },
  { value: 'WARNING', label: 'Aviso', icon: AlertTriangle, color: 'text-warning' },
  { value: 'CRITICAL', label: 'Crítico', icon: AlertCircle, color: 'text-danger' },
];

function getTypeBadge(type: NotificationType) {
  switch (type) {
    case 'INFO':
      return { bg: 'bg-accent-1/15 text-accent-1', label: 'Informativo' };
    case 'UPDATE':
      return { bg: 'bg-success/15 text-success', label: 'Atualização' };
    case 'WARNING':
      return { bg: 'bg-warning/15 text-warning', label: 'Aviso' };
    case 'CRITICAL':
      return { bg: 'bg-danger/15 text-danger', label: 'Crítico' };
    default:
      return { bg: 'bg-bg-3 text-text-3', label: type };
  }
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function NotificationManagement() {
  const { accessToken, user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  // Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form
  const [formTitle, setFormTitle] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [formType, setFormType] = useState<NotificationType>('INFO');
  const [formTenantId, setFormTenantId] = useState('');

  // Feedback
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isSuperAdmin = currentUser?.role === 'super_admin';

  useEffect(() => {
    if (isSuperAdmin) {
      loadNotifications();
      loadTenants();
    }
  }, [accessToken, isSuperAdmin, currentPage]);

  const loadNotifications = async () => {
    if (!accessToken) return;
    try {
      setLoading(true);
      const response = await apiClient.getAllNotifications(accessToken, currentPage, 15);
      if (response.success) {
        setNotifications(response.data.items);
        setTotalPages(response.data.totalPages);
      }
    } catch (err) {
      console.error('Erro ao carregar notificações:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    if (!accessToken) return;
    try {
      const response = await apiClient.getAllTenants(accessToken);
      if (response.success && response.data?.data) {
        setTenants(response.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar tenants:', err);
    }
  };

  const handleCreate = async () => {
    if (!accessToken) return;

    setError('');
    setSuccess('');

    if (!formTitle.trim()) {
      setError('Título é obrigatório');
      return;
    }
    if (!formMessage.trim()) {
      setError('Mensagem é obrigatória');
      return;
    }

    try {
      setSaving(true);
      const response = await apiClient.createNotification(
        {
          title: formTitle.trim(),
          message: formMessage.trim(),
          type: formType,
          targetTenantId: formTenantId || undefined,
        },
        accessToken,
      );

      if (response.success) {
        setSuccess('Notificação criada com sucesso!');
        setShowCreateModal(false);
        resetForm();
        await loadNotifications();
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao criar notificação');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormTitle('');
    setFormMessage('');
    setFormType('INFO');
    setFormTenantId('');
  };

  const openCreateModal = () => {
    resetForm();
    setError('');
    setShowCreateModal(true);
  };

  // Permission check
  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-bg-1">
        <Header title="Gerenciamento de Notificações" />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-bg-2 border border-error/30 rounded-lg p-8 text-center">
            <Shield size={48} className="mx-auto text-error mb-4" />
            <h2 className="text-xl font-semibold text-text-1 mb-2">Acesso Negado</h2>
            <p className="text-text-2">Apenas SUPER_ADMIN pode gerenciar notificações.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-1">
      <Header title="Gerenciamento de Notificações" subtitle="Criar e visualizar avisos para os usuários" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Messages */}
        {error && (
          <div className="mb-4 p-3 bg-error/10 border border-error/30 rounded-lg flex items-center gap-2 text-error">
            <AlertCircle size={18} />
            {error}
            <button onClick={() => setError('')} className="ml-auto">
              <X size={16} />
            </button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-success/10 border border-success/30 rounded-lg flex items-center gap-2 text-success">
            <CheckCircle size={18} />
            {success}
            <button onClick={() => setSuccess('')} className="ml-auto">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Actions Bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-text-2 text-sm">
            {!loading && `${notifications.length} notificação(ões) nesta página`}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadNotifications}
              className="flex items-center gap-2 px-3 py-2 bg-bg-2 border border-bg-3 rounded-lg text-text-2 hover:bg-bg-3 transition-colors text-sm"
            >
              <RefreshCw size={16} />
              Atualizar
            </button>
            <button
              onClick={openCreateModal}
              className="flex items-center gap-2 px-4 py-2 bg-accent-1 text-white rounded-lg hover:bg-accent-2 transition-colors text-sm font-medium"
            >
              <Plus size={16} />
              Nova Notificação
            </button>
          </div>
        </div>

        {/* Notifications Table */}
        <div className="bg-bg-2 border border-bg-3 rounded-lg overflow-hidden">
          {loading ? (
            <LoadingSpinner text="Carregando..." />
          ) : notifications.length === 0 ? (
            <div className="p-12 text-center">
              <Bell size={48} className="mx-auto text-text-3 mb-4" />
              <h3 className="text-lg font-medium text-text-1 mb-1">Nenhuma notificação</h3>
              <p className="text-text-3 text-sm">Crie sua primeira notificação para informar os usuários.</p>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-bg-3 bg-bg-3/30">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-text-3 uppercase tracking-wider">Tipo</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-text-3 uppercase tracking-wider">Título</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-text-3 uppercase tracking-wider hidden md:table-cell">Mensagem</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-text-3 uppercase tracking-wider">Destino</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-text-3 uppercase tracking-wider">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {notifications.map((notification) => {
                    const badge = getTypeBadge(notification.type);
                    const tenant = notification.targetTenantId
                      ? tenants.find((t) => t.id === notification.targetTenantId)
                      : null;

                    return (
                      <tr key={notification.id} className="border-b border-bg-3/50 hover:bg-bg-3/20 transition-colors">
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-text-1 truncate max-w-[200px]">{notification.title}</p>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <p className="text-sm text-text-2 truncate max-w-[300px]">{notification.message}</p>
                        </td>
                        <td className="px-4 py-3">
                          {notification.targetTenantId ? (
                            <span className="inline-flex items-center gap-1 text-xs text-text-2">
                              <Building2 size={12} />
                              {tenant?.name || 'Tenant'}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-accent-1">
                              <Globe size={12} />
                              Global
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-text-3">{formatDate(notification.createdAt)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 py-4 border-t border-bg-3">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm rounded-md bg-bg-3 text-text-2 hover:bg-bg-3/80 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <span className="text-sm text-text-3">
                    Página {currentPage} de {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm rounded-md bg-bg-3 text-text-2 hover:bg-bg-3/80 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próxima
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-bg-1 rounded-xl shadow-2xl w-full max-w-lg">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-bg-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-accent-1/10 rounded-lg">
                  <Send size={20} className="text-accent-1" />
                </div>
                <h2 className="text-lg font-semibold text-text-1">Nova Notificação</h2>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1 rounded-md hover:bg-bg-3 transition-colors"
              >
                <X size={20} className="text-text-3" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 space-y-4">
              {/* Error inside modal */}
              {error && (
                <div className="p-3 bg-error/10 border border-error/30 rounded-lg flex items-center gap-2 text-error text-sm">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-text-1 mb-1.5">Tipo</label>
                <div className="grid grid-cols-4 gap-2">
                  {TYPE_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    const isSelected = formType === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setFormType(opt.value)}
                        className={`flex flex-col items-center gap-1 p-2.5 rounded-lg border-2 text-xs font-medium transition-all ${
                          isSelected
                            ? 'border-accent-1 bg-accent-1/5'
                            : 'border-bg-3 hover:border-bg-3/80'
                        }`}
                      >
                        <Icon size={18} className={opt.color} />
                        <span className="text-text-2">{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-text-1 mb-1.5">Título</label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="Ex: Atualização de conta"
                  maxLength={255}
                  className="w-full px-3 py-2 border border-bg-3 rounded-lg bg-bg-1 text-text-1 placeholder-text-3 focus:outline-none focus:ring-2 focus:ring-accent-1/50 focus:border-accent-1 text-sm"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-text-1 mb-1.5">Mensagem</label>
                <textarea
                  value={formMessage}
                  onChange={(e) => setFormMessage(e.target.value)}
                  placeholder="Descreva a notificação..."
                  rows={3}
                  className="w-full px-3 py-2 border border-bg-3 rounded-lg bg-bg-1 text-text-1 placeholder-text-3 focus:outline-none focus:ring-2 focus:ring-accent-1/50 focus:border-accent-1 text-sm resize-none"
                />
              </div>

              {/* Target */}
              <div>
                <label className="block text-sm font-medium text-text-1 mb-1.5">Destinatário</label>
                <select
                  value={formTenantId}
                  onChange={(e) => setFormTenantId(e.target.value)}
                  className="w-full px-3 py-2 border border-bg-3 rounded-lg bg-bg-1 text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1/50 focus:border-accent-1 text-sm"
                >
                  <option value="">Todos os usuários (Global)</option>
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-text-3">
                  {formTenantId
                    ? 'Apenas usuários desta empresa verão a notificação.'
                    : 'Todos os usuários do sistema verão esta notificação.'}
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-bg-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm text-text-2 hover:text-text-1 hover:bg-bg-3 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreate}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-accent-1 hover:bg-accent-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Enviar Notificação
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
